/* eslint-disable jsx-a11y/no-static-element-interactions */
// @ts-nocheck

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
// styles
import ComparisonCheckbox from '@jo-v1/components/CompareCheckbox';
import { SchoolConfigData } from '@jo-v1/components/ExitStrategy/type';
import ProductFlags from '@jo-v3/components/ProductFlags';
import { useEffect, useState, useMemo, memo, type MouseEventHandler, useRef } from 'react';
import { FaStar } from 'react-icons/fa';
import { HiLocationMarker } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import useCheckScreen from '@core/blocks/eduResults/hooks/useCheckScreen';
import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import useProductEvents from '@core/hooks/cohesion/useProductEvents';
import useIntersectionObserver from '@core/hooks/useIntersectionObserver';
// selectors, actions
import {
  selectRecommenderPopupListId,
  setHeclidMapEntry,
  setProductCorrelationIdMapEntry,
  setViewCorrelationIdMapEntry,
  selectHeclidMap,
  selectProductCorrelationIdMap,
} from '@core/reducers/eventingSlice';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import {
  setLeadDelivery,
  selectLeadDelivery,
  setCurrentProgram,
  selectSubmissions,
  setShowDuplicatePopup,
  selectIsRecommenderPopupOpen,
  selectRecommenderMatches,
} from '@core/reducers/matchesSlice';
import { mobiusGetApplicationCta, mobiusRedirect } from '@core/services/mobius';
import { getSchoolConfig } from '@core/services/schoolConfig';
import type { VoyagerResult } from '@core/ts/results';
// hooks & utils
import cn from '@core/utils/classNames';
import getToken from '@core/utils/getToken';

import SchoolProgramTabs from '../SchoolProgramTabs';
import styles from './RecommenderCard.module.css';

type RecommenderCardProps = {
  result: VoyagerResult;
  setRecommenderStep?: (step: string) => void;
  position?: number;
  setLoading?: (loading: boolean) => void;
  eventingOverrides?: any;
  index?: number;
  withImage?: boolean;
  withTopBorder?: boolean;
  fullWidth?: boolean;
  onRecommender?: boolean;
  layout?: 'column' | 'row' | 'row-reverse' | 'column-reverse';
};

const RecommenderCard = ({
  result,
  setRecommenderStep,
  position,
  setLoading,
  eventingOverrides,
  index,
  withImage = false,
  fullWidth = false,
  withTopBorder = true,
  onRecommender = true,
  layout = 'column',
}: RecommenderCardProps) => {
  const { currentPrograms } = useSelector(selectLeadDelivery);

  // dispatches actions
  const dispatch = useDispatch();

  // local state
  const [schoolConfig, setSchoolConfig] = useState<SchoolConfigData>({});
  const [applicationCtaData, setApplicationCtaData] = useState<{ label: string; url: string } | null>(null);
  const cardRef = useRef<HTMLElement>(null);
  const [hasBeenViewed, setHasBeenViewed] = useState(false);
  // inputs state properties
  const inputs = useSelector(selectAllInputs);

  // Eventing Ids
  const popupListId = useSelector(selectRecommenderPopupListId);
  const newViewCorrelationId = useMemo(() => uuid(), []);

  // Store
  const heclidMap = useSelector(selectHeclidMap);
  const productCorrelationIdMap = useSelector(selectProductCorrelationIdMap);

  // determine if we are in single submission
  // DEV NOTE: setting this to just be true for the purposes of a desired optimization
  // and we don't want to remove the multi-select code just yet
  const isSingleSubmissionRecommender = true;
  const flags = useFeatureFlags();
  const isComparisonTest = !!flags.programsComparisonTest;
  const isRecommenderPopupOpen = useSelector(selectIsRecommenderPopupOpen);
  const recommenderMatches = useSelector(selectRecommenderMatches);
  let eventingLocation = 'voyager-results-page';
  if (isRecommenderPopupOpen) {
    eventingLocation = recommenderMatches.length === 1 ? 'voyager-spotlight-pop-up' : 'voyager-recommender-pop-up';
  } else if (layout === 'row') {
    eventingLocation = 'voyager-recommender-embedded';
  }

  const { isMobile } = useCheckScreen();

  // Initialize events
  const { productLoaded, productViewed, productClicked } = useProductEvents({
    listId: popupListId,
    viewCorrelationId: newViewCorrelationId,
    product: {
      sku: String(result?.cap?.id),
      variant: result?.program?.degree?.slug,
      productId: String(result?.program?.id),
      name: result?.program?.subject?.slug,
      category: result?.program?.category?.slug,
      brand: result?.school?.slug,
      position,
      location: eventingLocation,
      formatType: 'app',
      formatSubtype: 'jo-grid',
      positionEngine: 'algolia',
      ...eventingOverrides?.product,
    },
    customDimensions: eventingOverrides?.customDimensions,
  });

  const isInView = useIntersectionObserver(cardRef);
  const shouldFireProductViewedInPopup = isRecommenderPopupOpen && onRecommender;
  const shouldFireProductViewedInEmbedded = !isRecommenderPopupOpen && layout === 'row';

  // Fire productViewed event
  useEffect(() => {
    if ((isRecommenderPopupOpen && layout === 'column' && isInView) || (!isRecommenderPopupOpen && isInView)) {
      productViewed();
    }
  }, [isRecommenderPopupOpen, isInView]);

  // Fire productLoaded event and adds entry to the viewCorrelationIdMap
  useEffect(() => {
    productLoaded();
    if (result?.program?.id) {
      dispatch(
        setViewCorrelationIdMapEntry({
          key: result.program.id,
          value: newViewCorrelationId,
        })
      );
    }
  }, []);

  useEffect(() => {
    const getConfig = async () => {
      const schoolConfig = await getSchoolConfig(result?.school.id);
      setSchoolConfig(schoolConfig);
    };
    getConfig();
  }, [result]);

  const onBtnClickHandler = async (event) => {
    setLoading(true);
    dispatch(setCurrentProgram(result));

    const newProductCorrelationId = uuid();

    // add correlationId to map for LDS lead submission
    if (result?.program?.id) {
      dispatch(
        setProductCorrelationIdMapEntry({
          key: result.program.id,
          value: newProductCorrelationId,
        })
      );
    }

    productClicked({
      correlationId: newProductCorrelationId,
    });

    // Mobius Redirect will fire the OutcomeTracked APP_VIEWED event
    const { heclid } = await mobiusRedirect({
      idToken: getToken(),
      productCorrelationId: newProductCorrelationId,
      link: result?.url,
      trackingContextOverride: {
        formatType: 'app',
        formatSubtype: 'grid',
      },
    });
    // add heclid to map for Mobius track if API call succeeds
    if (result?.program?.id) {
      dispatch(setHeclidMapEntry({ key: result.program.id, value: heclid }));
    }

    setLoading(false);
    setRecommenderStep('PII_CONFIRMATION');
    // }
  };

  // Matches State properties
  const submissions = useSelector(selectSubmissions);
  const isProgramSubmitted = submissions.some((s) => s.program.id === result.program.id);
  const isSchoolSubmitted = !!submissions?.find((submission) => submission.school.id === result?.school.id);

  const handleProductClick = () => {
    const productCorrelationId = uuid();
    productClicked({ correlationId: productCorrelationId });
    // If school is already submitted, show duplicate popup
    if (isSchoolSubmitted) {
      dispatch(
        setLeadDelivery({
          show: true,
          currentPrograms: [result],
          showExitStep: true,
        })
      );
      dispatch(setShowDuplicatePopup(true));
      return;
    }

    // If program hasn't been submitted, show lead delivery slide-out
    dispatch(setProductCorrelationIdMapEntry({ key: result.program.id, value: productCorrelationId }));

    dispatch(
      setLeadDelivery({
        show: true,
        currentPrograms: [result],
        showExitStep: false,
      })
    );
  };

  const actionButton = useMemo(
    () =>
      !isProgramSubmitted ? (
        <button data-testid={`recommender-popup-card-button-${position}`} type="button" className={styles.cardBtnCta}>
          Explore Program
        </button>
      ) : (
        <button
          data-testid={`recommender-popup-card-button-${position}`}
          type="button"
          className={styles.cardBtnCtaSubmitted}
        >
          {applicationCtaData?.label || 'Start Your Application'}
        </button>
      ),
    [isProgramSubmitted, applicationCtaData]
  );

  useEffect(() => {
    const getApplicationCtaData = async () => {
      const response = await mobiusGetApplicationCta({
        programId: `${result.program.id}`,
        heclid: heclidMap[result.program.id],
        correlationId: productCorrelationIdMap[result.program.id],
      });
      setApplicationCtaData({ label: response?.label, url: response?.url });
    };

    getApplicationCtaData();
  }, []);

  const handleClick = () => {
    if (isProgramSubmitted && applicationCtaData?.url) {
      return window.open(applicationCtaData.url, '_blank');
    }
    if (onRecommender) {
      return onBtnClickHandler();
    }
    return handleProductClick();
  };

  return (
    <section
      ref={cardRef}
      className={cn(styles.card, styles[`layout-${layout}`], fullWidth ? styles.fullWidthCard : undefined)}
      onClick={handleClick}
    >
      {withTopBorder && (
        <div className={styles.cardTopBorder}>
          <div className={styles.cardTopBorderInner}>
            <div className={styles.bestMatchWrapper}>
              <span className={styles.starIconContainer}>
                <FaStar size={11} />
              </span>
              {index === 0 ? 'Best Match' : 'Great Match'}
            </div>
            <div className={styles.logo}>
              <img
                src={
                  result?.school?.slug
                    ? `https://simple-storage-server.highereducation.com/${result?.school.slug}.png?w=130&h=80`
                    : undefined
                }
                alt={result?.school?.name}
              />
            </div>
            <div className={styles.schoolInfo}>
              <h4 className={styles.title}>{result?.school?.name}</h4>
              {result?.school?.city && result?.school?.state && (
                <div className={styles.location}>
                  <span className={styles.locationIcon}>
                    <HiLocationMarker size={16} />
                  </span>{' '}
                  {result?.school.city}, {result?.school.state}
                </div>
              )}
            </div>
            {layout === 'row' && (
              <>
                <div className={styles.topBorderSpacer} />
                {actionButton}
              </>
            )}
          </div>
        </div>
      )}
      {withImage && <img className={styles.heroImage} src={schoolConfig?.image} alt={result?.school.name} />}
      <div className={styles.bodyContainer}>
        <div className={styles.cardBody}>
          <div
            className={cn(
              styles.cardBodyInner,
              isComparisonTest && !onRecommender && layout === 'row' && styles.comparisonCardBodyInner
            )}
          >
            <div>
              <h3 className={styles.programName}>{result?.program?.name}</h3>
              <ProductFlags flags={result?.school?.additional} />
            </div>
            <div
              className={cn(styles.ctaContainer, !isMobile && layout === 'row' && styles.ctaContainerDisplayContents)}
            >
              {actionButton} {!onRecommender && isComparisonTest && <ComparisonCheckbox result={result} />}
            </div>
          </div>

          <SchoolProgramTabs
            programSnippet={result?.program?.snippet}
            schoolAdditionalInfo={result?.school?.additional}
            layout={layout}
          />
        </div>
      </div>
    </section>
  );
};

// We use memo to prevent rerenders when props and state haven't changed
export default memo(RecommenderCard);
